/**
 * AP-36 Phase D — **die Speicherstelle einer Belegart** (`documentKind:<art>`).
 *
 * Eine Stelle je Art, geteilt von der Zeile der Einstellungstabelle und den
 * Optionen darunter (Mahnstufen, Preisgültigkeit, Lieferschein) — beide
 * schreiben dieselbe Zeile (7.2). Das PUT antwortet mit allen Einstellungen;
 * `werte` nimmt daraus die eigene Art.
 */
import { api } from '@/lib/axios'
import { mitStand, speicherRegister, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { preisgueltigkeitTage, type DocumentKind, type DocumentKindOptions } from '@symphenia/shared-types'

export const belegartSchluessel = (kind: DocumentKind | string) => `documentKind:${kind}`

/**
 * Aus der Antwort (alle Einstellungen, `{ data }` oder die Zeile selbst) die Zeile der Art.
 *
 * Die Preisgültigkeit ohne Eintrag gilt als Vorgabe der Art (Angebot 30 Tage).
 * In der Basis steht dann dieser Wert — sonst wäre „leer" (`null`, keine
 * Gültigkeit) vom fehlenden Eintrag nicht zu unterscheiden und ginge nie hinaus.
 */
export function belegartWerte(kind: string) {
  return (antwort: unknown): Datensatz => {
    const a = (antwort ?? {}) as Datensatz
    const innen = (a.data && typeof a.data === 'object' ? a.data : a) as Datensatz
    const arten = innen.kinds as Datensatz[] | undefined
    const zeile = (arten ? arten.find((k) => k.kind === kind) : innen) ?? {}
    const options = (zeile.options ?? {}) as DocumentKindOptions
    const vorgabe = preisgueltigkeitTage(kind as DocumentKind, options)
    if (options.priceValidityDays !== undefined || vorgabe === null) return zeile
    return { ...zeile, options: { ...options, priceValidityDays: vorgabe } }
  }
}

export function schreibeBelegart(kind: string, patch: Datensatz, stand?: string) {
  return api.put<Datensatz>(`/documents/settings/${kind}`, patch, mitStand(stand)).then((r) => r.data)
}

export function useBelegartStelle(kind: string, daten: Datensatz | null, bezeichnung: string): Speicherstelle<unknown> {
  return useSpeicherstelle<Datensatz>({
    schluessel: belegartSchluessel(kind),
    bezeichnung,
    daten,
    werte: belegartWerte(kind),
    schreibe: (patch, stand) => schreibeBelegart(kind, patch, stand),
    keepalive: { url: `/documents/settings/${kind}`, method: 'PUT' },
  }) as Speicherstelle<unknown>
}

/** Eine Antwort (alle Einstellungen) an die Stellen aller Arten weiterreichen. */
export function verteileBelegarten(antwort: Datensatz) {
  const innen = (antwort.data ?? antwort) as Datensatz
  for (const k of (innen.kinds as Datensatz[] | undefined) ?? []) {
    speicherRegister.stelle(belegartSchluessel(k.kind as string))?.uebernimm(antwort)
  }
}
